import React, { useEffect, useCallback, useState } from "react";
import { getAPIToken } from "utils/request";
function formatNumber(n) {
    return new Intl.NumberFormat().format(n);
}
function formatRelativeTime(iso) {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diffSec = Math.round((now - then) / 1000);
    if (diffSec < 60)
        return `${diffSec}s ago`;
    if (diffSec < 3600)
        return `${Math.round(diffSec / 60)}m ago`;
    if (diffSec < 86400)
        return `${Math.round(diffSec / 3600)}h ago`;
    return `${Math.round(diffSec / 86400)}d ago`;
}
function shortId(id) {
    return id.slice(0, 8);
}
export function UsageStats({ sandboxName }) {
    const [sessions, setSessions] = useState([]);
    const [totals, setTotals] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [expanded, setExpanded] = useState(false);
    const [lastFetchedAt, setLastFetchedAt] = useState(null);
    const fetchSessions = useCallback(async (isInitial) => {
        if (isInitial) {
            setLoading(true);
        }
        else {
            setRefreshing(true);
        }
        try {
            const apiToken = getAPIToken();
            const res = await fetch(`http://localhost:9000/sandboxes/${encodeURIComponent(sandboxName)}/sessions`, {
                headers: { Authorization: `Bearer ${apiToken}` }
            });
            if (!res.ok) {
                throw new Error(`Sessions request failed: ${res.status}`);
            }
            const data = await res.json();
            const sessionItems = data.filter((item) => item.type === "session");
            const totalItem = data.find((item) => item.type === "sessions_total") ?? null;
            sessionItems.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
            setSessions(sessionItems);
            setTotals(totalItem);
            setLastFetchedAt(Date.now());
            setError(null);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Unknown error");
        }
        finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [sandboxName]);
    useEffect(() => {
        fetchSessions(true);
    }, [fetchSessions]);
    if (loading && !totals) {
        return React.createElement("div", { className: "text-xs text-gray-500 px-1 py-2" }, "Loading usage\u2026");
    }
    if (error) {
        return (React.createElement("div", { className: "flex flex-col gap-2 px-1 py-2" },
            React.createElement("div", { className: "text-xs text-red-600" },
                "Failed to load usage: ",
                error),
            React.createElement(RefreshButton, { onClick: () => fetchSessions(false), refreshing: refreshing })));
    }
    const sessionCount = sessions.length;
    const visibleSessions = expanded ? sessions : sessions.slice(0, 3);
    return (React.createElement("div", { className: "flex flex-col gap-3 text-gray-800" },
        React.createElement("div", { className: "flex items-center justify-between px-1" },
            React.createElement("span", { className: "text-[10px] text-gray-400" }, lastFetchedAt ? `Updated ${formatRelativeTime(new Date(lastFetchedAt).toISOString())}` : ""),
            React.createElement(RefreshButton, { onClick: () => fetchSessions(false), refreshing: refreshing })),
        React.createElement("div", { className: "grid grid-cols-2 gap-2" },
            React.createElement(StatCard, { label: "Sessions", value: formatNumber(sessionCount) }),
            React.createElement(StatCard, { label: "Messages", value: formatNumber(totals?.message_count ?? 0) }),
            React.createElement(StatCard, { label: "Tool calls", value: formatNumber(totals?.tool_call_count ?? 0) }),
            React.createElement(StatCard, { label: "Tokens", value: formatNumber(totals?.total_tokens ?? 0) })),
        sessionCount > 0 && (React.createElement("div", { className: "flex flex-col gap-1" },
            React.createElement("div", { className: "text-[11px] uppercase tracking-wide text-gray-500 px-1" }, "Recent sessions"),
            React.createElement("ul", { className: `flex flex-col divide-y divide-gray-200 rounded-md border border-gray-200 bg-white ${expanded ? "max-h-72 overflow-y-auto" : ""}` }, visibleSessions.map((s) => (React.createElement("li", { key: s.id, className: "flex items-center justify-between gap-2 px-2 py-1.5 text-xs", title: `${s.id}\n${s.timestamp}` },
                React.createElement("div", { className: "flex flex-col min-w-0" },
                    React.createElement("span", { className: "font-mono text-gray-700 truncate" }, shortId(s.id)),
                    React.createElement("span", { className: "text-[10px] text-gray-500" }, formatRelativeTime(s.timestamp))),
                React.createElement("div", { className: "flex items-center gap-2 text-[11px] text-gray-600 shrink-0" },
                    React.createElement(Metric, { label: "msg", value: s.message_count }),
                    React.createElement(Metric, { label: "tool", value: s.tool_call_count }),
                    React.createElement(Metric, { label: "tok", value: s.total_tokens })))))),
            sessionCount > 3 && (React.createElement("button", { type: "button", onClick: () => setExpanded((v) => !v), className: "self-start text-[11px] text-gray-500 hover:text-gray-800 px-1 py-0.5" }, expanded ? "Show less" : `Show all ${sessionCount} sessions`))))));
}
function RefreshButton({ onClick, refreshing }) {
    return (React.createElement("button", { type: "button", onClick: onClick, disabled: refreshing, className: "inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed px-1.5 py-0.5 rounded hover:bg-gray-100", "aria-label": "Refresh" },
        React.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", className: `h-3 w-3 ${refreshing ? "animate-spin" : ""}`, "aria-hidden": "true" },
            React.createElement("path", { d: "M3 12a9 9 0 0 1 15.5-6.4L21 8" }),
            React.createElement("path", { d: "M21 3v5h-5" }),
            React.createElement("path", { d: "M21 12a9 9 0 0 1-15.5 6.4L3 16" }),
            React.createElement("path", { d: "M3 21v-5h5" })),
        refreshing ? "Refreshing" : "Refresh"));
}
function StatCard({ label, value }) {
    return (React.createElement("div", { className: "flex flex-col rounded-md border border-gray-200 bg-white px-2 py-1.5" },
        React.createElement("span", { className: "text-[10px] uppercase tracking-wide text-gray-500" }, label),
        React.createElement("span", { className: "text-sm font-medium text-gray-900 tabular-nums" }, value)));
}
function Metric({ label, value }) {
    return (React.createElement("span", { className: "tabular-nums" },
        React.createElement("span", { className: "text-gray-800" }, formatNumber(value)),
        React.createElement("span", { className: "ml-0.5 text-gray-400" }, label)));
}
